/**
 * App-wide feedback banner store.
 *
 * Backs the FeedbackZone banner. A zustand store (not React context) so it
 * can be fed from outside React — notably the QueryClient cache callbacks in
 * `src/api/serverErrorRouting.ts`, which classify 5xx errors and push the
 * "incidental" ones here.
 *
 * Every item auto-dismisses (success/warning quickly, error after a longer
 * read window — see AUTO_DISMISS_MS). `dismissOnRouteChange` additionally
 * clears everything on navigation, but never before MIN_DISPLAY_MS has
 * elapsed, so a banner that just appeared isn't yanked mid-navigation.
 */
import { create } from 'zustand';
import { registerStoreReset } from './registry';

export type FeedbackSeverity = 'success' | 'info' | 'error' | 'warning';

export interface FeedbackItem {
  id: string;
  message: string;
  severity: FeedbackSeverity;
  timestamp: number;
  /** How many times this identical banner has been pushed (>= 1). */
  count: number;
  /** Paint the banner over a white backing (Q-530 R8); see BANNERS_OPAQUE_BY_DEFAULT. */
  opaque?: boolean;
}

export interface FeedbackPushOptions {
  opaque?: boolean;
  /**
   * The action that raised this item (#1628): when the same action settles
   * again, `clearSource` removes it. A push with no source is permanent.
   */
  source?: string;
  /**
   * Dialog layers only: the opening the action ran in (#1174). The global
   * banner has no openings and ignores it.
   */
  opening?: number;
}

export interface FeedbackState {
  items: FeedbackItem[];
  /** Add a banner. Returns its id. */
  push: (message: string, severity: FeedbackSeverity, options?: FeedbackPushOptions) => string;
  /**
   * The action `source` succeeded: remove it from every item that carries it.
   * An item left with no source, and never pushed without one, is dismissed.
   */
  clearSource: (source: string) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  /** Clear everything on route change, respecting each item's minimum display time. */
  dismissOnRouteChange: () => void;
}

/** Every banner stays visible at least this long, even across a route change. */
const MIN_DISPLAY_MS = 2000;

const AUTO_DISMISS_MS: Record<FeedbackSeverity, number> = {
  success: 5000,
  // Calm confirmations ("already done, nothing to do") read like success.
  info: 5000,
  warning: 6000,
  // Errors get a longer read window than success/warning, but still expire
  // on their own — manual-only dismissal is the behavior we're replacing.
  error: 15000,
};

// Timers live outside the store — they are not render state.
const timers = new Map<string, ReturnType<typeof setTimeout>>();
let nextId = 0;
/**
 * The sources behind each sourced item (#1628). An id with no entry is
 * permanent (pushed without a source): `clearSource` never removes it.
 */
const sourcesById = new Map<string, Set<string>>();

function clearTimer(id: string): void {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t);
    timers.delete(id);
  }
}

export const useFeedbackStore = create<FeedbackState>((set, get) => ({
  items: [],

  push: (message, severity, options) => {
    const source = options?.source;
    // Deduplicate identical banners: stacking is fine for distinct messages,
    // but a recurring error (e.g. "Network Error" firing on every retry) should
    // collapse to one banner with a count rather than a tower of duplicates.
    const existing = get().items.find((i) => i.message === message && i.severity === severity);
    if (source !== undefined) {
      // The same action settling again replaces its earlier item (#1628).
      for (const [id, sources] of [...sourcesById]) {
        if (id === existing?.id || !sources.delete(source)) continue;
        if (sources.size === 0) get().dismiss(id);
      }
    }
    if (existing) {
      const sources = sourcesById.get(existing.id);
      if (sources) {
        if (source === undefined) sourcesById.delete(existing.id); // now permanent
        else sources.add(source);
      }
      set((s) => ({
        items: s.items.map((i) =>
          i.id === existing.id ? { ...i, count: i.count + 1, timestamp: Date.now() } : i,
        ),
      }));
      // Refresh the auto-dismiss window so the re-occurrence stays visible.
      const ms = AUTO_DISMISS_MS[severity];
      if (ms) {
        clearTimer(existing.id);
        timers.set(existing.id, setTimeout(() => get().dismiss(existing.id), ms));
      }
      return existing.id;
    }

    nextId += 1;
    const id = `fb-${nextId}`;
    if (source !== undefined) sourcesById.set(id, new Set([source]));
    set((s) => ({ items: [...s.items, {
        id, message, severity, timestamp: Date.now(), count: 1,
        ...(options?.opaque !== undefined && { opaque: options.opaque }),
      }] }));

    const ms = AUTO_DISMISS_MS[severity];
    if (ms) {
      timers.set(id, setTimeout(() => get().dismiss(id), ms));
    }
    return id;
  },

  clearSource: (source) => {
    for (const [id, sources] of [...sourcesById]) {
      if (sources.delete(source) && sources.size === 0) get().dismiss(id);
    }
  },

  dismiss: (id) => {
    clearTimer(id);
    sourcesById.delete(id);
    set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
  },

  dismissAll: () => {
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
    sourcesById.clear();
    set({ items: [] });
  },

  dismissOnRouteChange: () => {
    const now = Date.now();
    get().items.forEach((item) => {
      const elapsed = now - item.timestamp;
      if (elapsed >= MIN_DISPLAY_MS) {
        get().dismiss(item.id);
        return;
      }
      // Just appeared — let it finish out its minimum display time instead
      // of yanking it mid-navigation, then dismiss on schedule.
      clearTimer(item.id);
      timers.set(item.id, setTimeout(() => get().dismiss(item.id), MIN_DISPLAY_MS - elapsed));
    });
  },
}));

registerStoreReset(() => {
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  sourcesById.clear();
  useFeedbackStore.setState({ items: [] });
}, { onUserSwitch: false }); // banners the person would see vanish (#1336)
